import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import Immutable from 'immutable';
import * as Yup from 'yup';
import { FormikHelpers } from 'formik';
import {
  FormikWrapper,
  FormTextField as TextField,
} from '@uncinc/uncinc-react-kitchen-sink';
import {
  userUpdate,
  UI_OPERATION_STATUS_ERROR,
  UI_OPERATION_STATUS_REQUEST,
  UI_OPERATION_STATUS_SUCCESS,
  setOperationStatus,
} from '@uncinc/react-drupal-core';
import { ImmutableRootState } from '@uncinc/react-drupal-core/types';

import { Button, CoreLink } from '../../../components';
import { ROUTE_TYPE_GUIDED_SELF_HELP } from '../../../config/constants';
import Page from '../../../containers/Page';
import useClasses from '../../../hooks/useClasses';
import useOperationStatus from '../../../hooks/useOperationStatus';
import useRoutes from '../../../hooks/useRoutes';
import useToast from '../../../hooks/useToast';
import useTypedDispatch from '../../../hooks/useTypedDispatch';

import './style.scss';

type NursePractitionerUser = Immutable.Map<string, Immutable.List<Immutable.Map<string, string>>>;

const PageNursePractitionerAccountDetails: React.FC = () => {
  const dispatch = useTypedDispatch();
  const { t } = useTranslation();
  const toast = useToast();

  const { account } = useRoutes(ROUTE_TYPE_GUIDED_SELF_HELP);
  const { buildClass } = useClasses('PageNursePractitionerAccountDetails');

  const pohId = useSelector<ImmutableRootState<number>, number>((state) => state.getIn(['login', 'uid']));
  const pohDetails = useSelector<ImmutableRootState<NursePractitionerUser>, NursePractitionerUser>((state) => state.getIn(['user', pohId], Immutable.Map()));

  const operationStatus = useOperationStatus('user', 'update');

  useEffect(() => {
    if (operationStatus === UI_OPERATION_STATUS_SUCCESS) {
      toast.show(t('pages.nurse_practitioner_account.success_message'), 'success');
    }
    if (operationStatus === UI_OPERATION_STATUS_ERROR) {
      toast.show(t('pages.nurse_practitioner_account.error_message'), 'error');
    }

    if (operationStatus !== UI_OPERATION_STATUS_REQUEST) {
      // Consume the operation status so the toast won't show again.
      dispatch(setOperationStatus('user', 'update', null));
    }
  }, [operationStatus, dispatch, toast, t]);

  const fields = [
    'field_initials',
    'field_family_name',
    'field_function',
    'field_general_practitioner',
    'field_address',
    'field_postal_code',
    'field_locality',
    'field_telephone_number',
  ];

  const initialValues = Object.fromEntries(
    fields.map((field) => ([field, pohDetails.getIn([field, 0, 'value'], '')])),
  );

  const validationSchema = Yup.object(
    Object.fromEntries(
      fields.map((field) => {
        if (field === 'field_postal_code') {
          return [
            field,
            Yup.string()
              .trim()
              .matches(/^[1-9][0-9]{3}\s*(?!sa|sd|ss)[a-z]{2}$/i, t(`pages.nurse_practitioner_account.fields.${field}.validation.invalid`))
              .required(t(`pages.nurse_practitioner_account.fields.${field}.validation.required`)),
          ];
        }

        return [
          field,
          Yup.string()
            .trim()
            .required(t(`pages.nurse_practitioner_account.fields.${field}.validation.required`)),
        ];
      }),
    ),
  );

  type FormValues = Yup.InferType<typeof validationSchema>;

  const onSubmit = (values: FormValues, formik: FormikHelpers<FormValues>) => {
    const data = Object.fromEntries(
      Object.entries(values).map(
        ([key, value]) => [key, [{ value }]],
      ),
    );

    formik.setSubmitting(false);
    dispatch(userUpdate(pohId, data));
  };

  return (
    <Page className={buildClass()} title={t('pages.nurse_practitioner_account.title')}>
      <div className="col-md-8 offset-md-2 col-lg-6 col-lg-1 py-8 py-lg-12">
        <main className={buildClass('main')}>
          <h1 className={buildClass('title')}>
            {t('pages.nurse_practitioner_account.title')}
          </h1>
          <div className={buildClass('form-wrapper')}>
            <FormikWrapper
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={onSubmit}
              validateOnBlur={false}
              validateOnChange={false}
            >
              {fields.map((field) => (
                <TextField
                  title={t(`pages.nurse_practitioner_account.fields.${field}.label`)}
                  fieldName={field}
                />
              ))}

              <div className={buildClass('buttons')}>
                <Button
                  htmlType="submit"
                  buttonState={operationStatus === UI_OPERATION_STATUS_REQUEST && 'disabled'}
                >
                  {t('buttons.save')}
                </Button>
              </div>
            </FormikWrapper>
          </div>

          <div className={buildClass('links')}>
            <CoreLink to={account.email}>{t('pages.nurse_practitioner_change_email.title')}</CoreLink>
            <CoreLink to={account.password}>{t('pages.nurse_practitioner_change_password.title')}</CoreLink>
          </div>
        </main>
      </div>
    </Page>
  );
};

export default PageNursePractitionerAccountDetails;
